import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";

// Shared look of the machine SBOM screens: light enterprise theme, colours from src/index.css variables.

export const cardSx = {
  backgroundColor: "var(--bg-card)",
  border: "1px solid var(--border)",
  borderRadius: "8px",
  boxShadow: "var(--shadow-sm)",
  p: 3,
};

export const primaryBtnSx = {
  textTransform: "none",
  fontWeight: 600,
  backgroundColor: "var(--btn-primary)",
  color: "#ffffff",
  boxShadow: "none",
  "&:hover": { backgroundColor: "var(--btn-primary-hover)", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "var(--border)", color: "var(--text-muted)" },
  "&:focus-visible": { outline: "2px solid var(--accent-navy-light)", outlineOffset: 2 },
};

export const outlineBtnSx = {
  textTransform: "none",
  color: "var(--text-primary)",
  borderColor: "var(--border-strong)",
  "&:hover": { backgroundColor: "var(--bg-hover)", borderColor: "var(--border-strong)" },
  "&.Mui-disabled": { color: "var(--text-muted)", borderColor: "var(--border)", opacity: 0.7 },
  "&:focus-visible": { outline: "2px solid var(--accent-navy-light)", outlineOffset: 2 },
};

export const dangerBtnSx = { ...outlineBtnSx, color: "var(--danger)" };

export const headCellSx = { color: "var(--text-muted)", fontWeight: 600, backgroundColor: "var(--bg-surface)", borderColor: "var(--border)", whiteSpace: "nowrap" };
export const cellSx = { color: "var(--text-primary)", borderColor: "var(--border)", verticalAlign: "top" };
export const fieldSx = { minWidth: 200, backgroundColor: "var(--bg-input)", "& .MuiInputBase-input, & .MuiSelect-select": { color: "var(--text-primary)" } };

// Light background + dark text (AA), always with a text label: colour is never the only signal.
const TONES = {
  critical: ["#fde8e8", "#9b1c1c"],
  high: ["#fdecd8", "#8a3a08"],
  medium: ["#fef3c7", "#6b3f0a"],
  low: ["#e0f2fe", "#0b4a73"],
  kev: ["#fce7f3", "#9d174d"],
  success: ["#dcfce7", "#14532d"],
  progress: ["var(--info-bg)", "var(--info-text)"],
  neutral: ["var(--bg-hover)", "var(--text-secondary)"],
};

export function Pill({ tone = "neutral", text }) {
  const [bg, fg] = TONES[tone] || TONES.neutral;
  return (
    <Box component="span" sx={{ display: "inline-block", backgroundColor: bg, color: fg, borderRadius: "999px", px: 1.25, py: "2px", fontSize: 12, fontWeight: 600, lineHeight: 1.5, whiteSpace: "nowrap" }}>
      {text}
    </Box>
  );
}

export function InfoNote({ children, role }) {
  return (
    <Box role={role} sx={{ backgroundColor: "var(--info-bg)", border: "1px solid var(--info-border)", color: "var(--info-text)", borderRadius: "8px", px: 2, py: 1.25, fontSize: 13, lineHeight: 1.55 }}>
      {children}
    </Box>
  );
}

export function WarnNote({ children, role }) {
  return (
    <Box role={role} sx={{ backgroundColor: "#fff7ed", border: "1px solid #fdba74", color: "#7c2d12", borderRadius: "8px", px: 2, py: 1.25, fontSize: 13, lineHeight: 1.55 }}>
      {children}
    </Box>
  );
}

export function Muted({ children, sx }) {
  return <Typography component="span" sx={{ color: "var(--text-muted)", fontSize: 13, lineHeight: 1.5, ...sx }}>{children}</Typography>;
}

export function Pager({ offset, shown, total, pageSize, onChange }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, gap: 2 }}>
      <Muted>{total ? `${offset + 1}–${offset + shown} / ${total}` : ""}</Muted>
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button size="small" variant="outlined" sx={outlineBtnSx} disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - pageSize))}>{t("common.previous")}</Button>
        <Button size="small" variant="outlined" sx={outlineBtnSx} disabled={offset + pageSize >= total} onClick={() => onChange(offset + pageSize)}>{t("common.next")}</Button>
      </Box>
    </Box>
  );
}
